import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { v4 as uuid } from 'uuid';
import { Typography } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import GalleryLayout from 'layouts/GalleryLayout';
import { useBreadcrumbsContext } from 'contexts/breadcrumbsContext';
import createSlug from 'utils/createSlug';
import IllustrationsService from 'services/IllustrationsService';
import mapIllustrationProps from 'utils/mapIllustrationProps';
import GalleryGrid from 'components/GalleryGrid';
import IllustrationThumb from 'components/IllustrationThumb';
import { TITLE } from 'config/app';
import capitalizeString from 'utils/capitalizeString';
import StudyFieldCategoriesService from 'services/StudyFieldCategoriesService';
import { ShowMoreContainer } from 'layouts/GalleryLayout/styles';
import Button from 'components/Button';
import useScrollOffset from 'hooks/useScrollOffset';
import { setLocale } from 'utils/locale';
import { useQuery } from '@tanstack/react-query';
import { getEnv } from 'utils';

import en from '../../../locales/illustrations/en';
import ja from '../../../locales/illustrations/en';
import zh from '../../../locales/illustrations/en';
import es from '../../../locales/illustrations/es';
import fr from '../../../locales/illustrations/fr';
import it from '../../../locales/illustrations/it';

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params: { slug }, locale }) {
  try {
    const { data: subcategory } = await StudyFieldCategoriesService.fetchOne(slug);
    const { data } = await IllustrationsService.search(
      subcategory.tags.split(',').join(' '),
      locale,
      {
        limit: 100,
      },
    );

    return {
      props: {
        initialIllustrations: data.images.map(mapIllustrationProps),
        tags: subcategory.tags.split(',').join(' '),
      },
      revalidate: 604800, // 1 week
    };
  } catch (error) {
    console.log(error.message);
    return {
      props: {
        initialIllustrations: [],
        tags: '',
      },
      revalidate: 604800, // 1 week
    };
  }
}

export default function Subcategory({ initialIllustrations, tags }) {
  const router = useRouter();
  const {
    query: { slug, breadcrumbs },
  } = router;
  const [, dispatchBreadcrumbs] = useBreadcrumbsContext();
  const [start, setStart] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollOffset = useScrollOffset();
  const { locale } = router;
  const isMobile = useIsMobile();
  const t = setLocale(locale, { en, es, fr, it, ja, zh });

  const labelText = createSlug(slug, true);

  const schemaData = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://mindthegraph.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Illustrations',
        item: 'https://mindthegraph.com/illustrations',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: capitalizeString(labelText),
        item: `${getEnv('MTG_URL_APP')}${router.asPath}`,
      },
    ],
  };

  const { data } = useQuery({
    queryKey: ['initialIllustrations'],
    queryFn: async () => {
      return StudyFieldCategoriesService.fetchOne(slug);
    },
    initialData: initialIllustrations,
    retryOnMount: false,
  });

  const [illustrations, setIllustrations] = useState(data);

  const { data: tagsData } = useQuery({
    queryKey: ['tagsDataTemplates'],
    queryFn: async () => {
      await IllustrationsService.search(data.tags.split(',').join(' '), locale, {
        limit: 100,
      });
    },
    initialData: tags,
    retryOnMount: false,
  });

  const fetchIllustrationsQuery = useQuery({
    queryKey: ['tagsDataLimit'],
    queryFn: async () => {
      return await IllustrationsService.search(tagsData, locale, {
        limit: 100,
        start,
      });
    },
    initialData: initialIllustrations,
    retryOnMount: false,
  });

  useEffect(() => {
    async function fetchIllustrations() {
      setIsLoadingMore(true);

      try {
        const { data: response } = await fetchIllustrationsQuery.refetch();

        setIllustrations([...illustrations, ...response.data.images.map(mapIllustrationProps)]);

        setTimeout(() => {
          if (start > 100) {
            window.scroll(0, scrollOffset);
          }
        }, 100);
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsLoadingMore(false);
      }
    }

    if (start) {
      fetchIllustrations();
    }
  }, [start]);

  useEffect(() => {
    const updateBreadcrumbs = () => {
      const label = createSlug(slug, true);

      if (!breadcrumbs) {
        dispatchBreadcrumbs([
          {
            label: 'Illustrations',
            href: '/illustrations',
          },
          {
            label,
            href: `/illustrations/subcategory/${slug}`,
            isDisabled: true,
          },
        ]);
        return;
      }

      const breadcrumbsList = [
        {
          label: 'Illustrations',
          href: '/illustrations',
        },
      ];

      if (breadcrumbs) {
        breadcrumbsList.push({
          label: capitalizeString(breadcrumbs),
          href: `/illustrations/subcategory/${slug}`,
        });
      }

      breadcrumbsList.push({
        label,
        href: `/illustrations/subcategory/${slug}`,
        isDisabled: true,
      });

      dispatchBreadcrumbs(breadcrumbsList);
    };

    if (slug) {
      updateBreadcrumbs();
    }
  }, [slug]);

  return (
    <GalleryLayout category={'gallery'} label={slug}>
      <Head>
        <title>
          {t[capitalizeString(slug)] || capitalizeString(slug)} Icons, Illustrations & Images for
          Free - {TITLE}
        </title>

        <meta
          name="description"
          content={`Find ${t[capitalizeString(slug)] || capitalizeString(slug)} icons, illustrations, and images for free with Mind the Graph. Access 75,000+ accurate visuals for research, posters, and presentations!`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>

      <p>
        <strong>{t[capitalizeString(slug)]}</strong>
      </p>

      <Typography variant={!isMobile ? 'h5' : 'body2'} component="h1">
        {t[capitalizeString(slug)] || capitalizeString(slug)} Icons and Illustrations
      </Typography>

      <p>{`${t.checkOutText} ${t[capitalizeString(slug)] || capitalizeString(slug)} ${t.createUniqueText} ${
        t[capitalizeString(slug)] || capitalizeString(slug)
      } ${t.infographicsText}`}</p>

      <GalleryGrid>
        {illustrations.map((illustration) => {
          if (illustration.tags.some((tag) => ['penis', 'vagina'].includes(tag))) {
            return null;
          }

          return (
            <IllustrationThumb
              key={uuid()}
              {...illustration}
              href={`${illustration.href}?breadcrumbs=${
                breadcrumbs ? `${breadcrumbs},` : ''
              }${slug}&from=subcategory`}
            />
          );
        })}
      </GalleryGrid>

      {illustrations.length < 100 ? null : (
        <ShowMoreContainer>
          <Button isLoading={isLoadingMore} onClick={() => setStart(illustrations.length + 1)}>
            {t.showMore}
          </Button>
        </ShowMoreContainer>
      )}
    </GalleryLayout>
  );
}
